import createScopedHubHandlers, { hubUrl } from './hub';
import createClientHandlers from './client';
import {
  NOTIFICATIONS,
  NOTIFICATION_TYPE,
  NOTIFICATIONS_TO_BE_CLEARED,
} from './notifications';
import { AUCTION_EVENTS } from '../event_bus_modules/auction_bidding';
import { appInsights } from '../services/appinsights.service';
import {
  DEFAULT_TIMEOUT,
  NO_TIMEOUT,
  getBidIncrementValue,
  RETRY_TIMEOUT,
} from './utils';
import { WINDOW_EVENTS } from '../event_bus_modules/window';

$.ajaxSetup({ cache: false });
export const connectToHub = ({
  auctionPropertyId,
  auctionId,
  userBidderId,
  jwtToken,
  djangoLanguage,
}) => {
  // hub setup
  const hub = $.connection.hub;
  const bidHub = $.connection.bidHub;

  hub.url = hubUrl;

  const traceId = document
    .querySelector('meta[name="trace-id"]')
    ?.getAttribute('content');
  const spanId = document
    .querySelector('meta[name="span-id"]')
    ?.getAttribute('content');

  const queryParams = {
    jwt: jwtToken,
    django_language: djangoLanguage,
  };

  if (traceId) {
    queryParams.trace_id = traceId;
  }
  if (spanId) {
    queryParams.span_id = spanId;
  }

  hub.qs = queryParams;
  hub.logging = true;

  const clientConnected = function (auctionPropertyId) {
    // no action
  };

  const server = $.connection.bidHub.server;
  bidHub.client.clientConnected = clientConnected;

  return new Promise((resolve, reject) => {
    hub
      .start({ transport: ['webSockets', 'longPolling'] })
      .done(() => {
        // load success
        EVENT_BUS.dispatch(AUCTION_EVENTS.CONNECTION_ESTABLISHED);
        // join to message group for this lot
        server.joinLotAuctionGroup(auctionId, auctionPropertyId);

        // maintain connection & reload page if connection fails
        if (hub.transport.name === 'longPolling') {
          // Ping every 10s
          setInterval(() => {
            // Ensure we're connected (don't want to be pinging in any other state).
            if (hub.state === $.signalR.connectionState.connected) {
              server.heartbeatPing().fail(() => {
                // Failed to ping the server
                EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
                  type: NOTIFICATION_TYPE.ERROR,
                  notification: NOTIFICATIONS.CONNECTION_LOST,
                });
                appInsights.trackEvent({
                  name: 'Connection Lost',
                  properties: {
                    AuctionPropertyId: auctionPropertyId,
                    Message: 'Connection Failed - Reloading',
                    BidderId: userBidderId,
                  },
                });
                setTimeout(reloadPage, DEFAULT_TIMEOUT);
              });
            }
          }, 1e4);
        }
        resolve();
      })
      .fail(() => {
        EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
          type: NOTIFICATION_TYPE.ERROR,
          notification: NOTIFICATIONS.CONNECTION_LOST,
        });
        appInsights.trackEvent({
          name: 'Connection Lost',
          properties: {
            AuctionPropertyId: auctionPropertyId,
            Message: 'Connection Failed - Could not connect to bidding',
            BidderId: userBidderId,
          },
        });
        reject();
      });
  });
};

export const initializeAuctionManager = ({
  auctionPropertyId,
  auctionId,
  userBidderId,
  jwtToken,
  djangoLanguage,
}) => {
  /*
   Signal-R connection setup
  */

  const newHubConnection = () => {
    connectToHub({
      auctionPropertyId,
      auctionId,
      userBidderId,
      jwtToken,
      djangoLanguage,
    });
  };

  const hubScriptNotLoaded = () => {
    return !!(
      $.connection === undefined ||
      $.connection === null ||
      $.connection.hub === undefined ||
      $.connection.hub === null ||
      $.connection.bidHub === undefined ||
      $.connection.bidHub === null ||
      $.connection.bidHub.server === undefined ||
      $.connection.bidHub.server === null ||
      $.connection.bidHub.client === undefined ||
      $.connection.bidHub.client === null
    );
  };

  if (hubScriptNotLoaded()) {
    console.log('Hub initialisation failed');
    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.ERROR,
      notification: NOTIFICATIONS.CONNECTION_LOST,
    });
  }

  const hub = $.connection.hub;
  const server = $.connection.bidHub.server;
  const client = $.connection.bidHub.client;

  let retryInterval;
  let isReloading = false;

  const stopBidRetry = () => {
    clearInterval(retryInterval);
  };

  const reloadPage = () => {
    if (!isReloading) {
      isReloading = true;
      hub.stop();
      location.reload();
    }
  };

  const assignHandlers = (module, handlers) => {
    Object.entries(handlers).forEach(([handlerName, handler]) => {
      module[handlerName] = handler;
    });
  };

  // Handlers for AuctionEvents
  EVENT_BUS.on(AUCTION_EVENTS.INSTANT_BID_CONFIRMED, ({ bidValue }) => {
    let bidAttemptCount = 1;

    server.placeBid(auctionPropertyId, bidValue).fail(() => {
      EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
        type: NOTIFICATION_TYPE.ERROR,
        notification: NOTIFICATIONS.BID_PROCESSING_ERROR,
      });
      appInsights.trackEvent({
        name: 'Error Processing Bid',
        properties: {
          AuctionPropertyId: auctionPropertyId,
          Message: 'bids.server.placeBid',
          BidAmount: bidValue,
          BidderId: userBidderId,
        },
      });
    });

    appInsights.trackEvent({
      name: 'Place Bid',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        Message: 'bids.server.placeBid',
        BidAmount: bidValue,
        BidderId: userBidderId,
      },
    });

    // setup bid retry attempt
    retryInterval = setInterval(() => {
      if (bidAttemptCount <= 3) {
        EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
          type: NOTIFICATION_TYPE.WARNING,
          notification: NOTIFICATIONS.RETRYING_BID,
          message: bidAttemptCount,
        });

        server.placeBid(auctionPropertyId, bidValue).fail(() => {
          EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
            type: NOTIFICATION_TYPE.ERROR,
            notification: NOTIFICATIONS.BID_PROCESSING_ERROR,
          });
          appInsights.trackEvent({
            name: 'Error Processing Bid',
            properties: {
              AuctionPropertyId: auctionPropertyId,
              Message: 'bids.server.placeBid.bid.reattempting',
              BidderId: userBidderId,
              BidAmount: bidValue,
              AttemptCount: bidAttemptCount,
            },
          });
        });

        appInsights.trackEvent({
          name: 'Place Bid - Retry',
          properties: {
            AuctionPropertyId: auctionPropertyId,
            BidAmount: bidValue,
            BidderId: userBidderId,
            BidAttempt: bidAttemptCount,
          },
        });
      } else {
        // TODO: Confirm the message that should be logged
        appInsights.trackEvent({ name: 'Force Page Reload' });
        stopBidRetry();
        reloadPage();
      }
      bidAttemptCount += 1;
    }, RETRY_TIMEOUT);
  });

  EVENT_BUS.on(AUCTION_EVENTS.INSTANT_BID_MODAL_OPENED, ({ bidValue }) => {
    appInsights.trackEvent({
      name: 'Place Instant Button Step 1',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        Message: bidValue,
        BidderId: userBidderId,
      },
    });
  });

  EVENT_BUS.on(AUCTION_EVENTS.AUTO_BID_CONFIRMED, ({ autoBidValue }) => {
    let bidAttemptCount = 1;

    server.placeIncrement(auctionPropertyId, autoBidValue).fail(() => {
      EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
        type: NOTIFICATION_TYPE.ERROR,
        notification: NOTIFICATIONS.PLACE_INCREMENT_ERROR,
      });
      appInsights.trackEvent({
        name: 'Error Processing Bid',
        properties: {
          AuctionPropertyId: auctionPropertyId,
          Message: 'bids.server.placeIncrement',
          BidAmount: autoBidValue,
          BidderId: userBidderId,
        },
      });
    });

    appInsights.trackEvent({
      name: 'Placed AutoBid',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        Message: 'bids.server.placeIncrement',
        BidAmount: autoBidValue,
        BidderId: userBidderId,
      },
    });

    retryInterval = setInterval(() => {
      if (bidAttemptCount <= 3) {
        EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
          type: NOTIFICATION_TYPE.WARNING,
          notification: NOTIFICATIONS.RETRYING_BID,
          message: bidAttemptCount,
        });

        server.placeIncrement(auctionPropertyId, autoBidValue).fail(() => {
          EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
            type: NOTIFICATION_TYPE.ERROR,
            notification: NOTIFICATIONS.BID_PROCESSING_ERROR,
          });
          appInsights.trackEvent({
            name: 'Error Processing Bid - Reattempting',
            properties: {
              AuctionPropertyId: auctionPropertyId,
              Message: 'bids.server.placeIncrement.bid.reattempting',
              BidderId: userBidderId,
              BidAmount: autoBidValue,
              BidAttemptCount: bidAttemptCount,
            },
          });
        });

        appInsights.trackEvent({
          name: 'Placed Auto Bid - Reattempting',
          properties: {
            AuctionPropertyId: auctionPropertyId,
            BidAmount: autoBidValue,
            BidderId: userBidderId,
            BidAttempt: bidAttemptCount,
          },
        });
      } else {
        stopBidRetry();
        reloadPage();
      }
      bidAttemptCount += 1;
    }, RETRY_TIMEOUT);
  });

  EVENT_BUS.on(AUCTION_EVENTS.AUTO_BID_MODAL_OPENED, ({ autoBidValue }) => {
    appInsights.trackEvent({
      name: 'Place Bid',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        BidAmount: autoBidValue,
        Message: 'Place AutoBid Button Step 1',
        BidderId: userBidderId,
      },
    });
  });

  EVENT_BUS.on(
    AUCTION_EVENTS.REQUEST_AUCTION_STATUS_UPDATE,
    ({ timeout = NO_TIMEOUT }) => {
      server.getAuctionStatus(auctionPropertyId).done((response) => {
        const {
          BidAmount,
          BidderId,
          BidderIds,
          BidPlacedDateTime,
          IsReserveMet,
          IsClosed,
          Bids,
          SecondsToClosing,
          IsInExtendedPeriod,
        } = response;

        const nextAmount = BidAmount + getBidIncrementValue();

        // dispatch global event with the latest state
        EVENT_BUS.dispatch(AUCTION_EVENTS.AUCTION_STATUS_UPDATED, {
          amount: BidAmount,
          nextAmount: nextAmount,
          bidderId: BidderId,
          bidderIds: BidderIds,
          bidTime: BidPlacedDateTime,
          reserveMet: IsReserveMet,
          isClosed: IsClosed,
          bids: Bids,
          secondsToClosing: SecondsToClosing,
          userBidderId,
          isInExtendedPeriod: IsInExtendedPeriod,
          timeout,
        });
      });
    }
  );

  EVENT_BUS.on(
    AUCTION_EVENTS.AUCTION_STATUS_UPDATED,
    ({ bidderId, timeout = NO_TIMEOUT, amount }, { bidValue }) => {
      const bidderIsCurrentUser = userBidderId === bidderId;
      // handle case when users instant bid is matched and outbidded by somebody's auto bid
      const amountIsEqualOrGreaterThanCurrentUserLastBid = amount >= bidValue;
      if (bidderIsCurrentUser || amountIsEqualOrGreaterThanCurrentUserLastBid) {
        setTimeout(
          () => EVENT_BUS.dispatch(AUCTION_EVENTS.CLEAR_NOTIFICATION),
          timeout
        );
        stopBidRetry();
      }
    }
  );

  EVENT_BUS.on(AUCTION_EVENTS.AUCTION_STATUS_UPDATED, (payload) => {
    setTimeout(() =>
      EVENT_BUS.dispatch(AUCTION_EVENTS.AFTER_AUCTION_STATUS_UPDATED, payload)
    );
  });

  const RETRY_DELAYS = [1000, 3000, 5000];

  const checkIfAuctionIsClosedAndUpdateUI = ({ retryCount = 0 } = {}) => {
    server.getAuctionStatus(auctionPropertyId).done((response) => {
      const {
        IsClosed,
        BidderId,
        BidderIds,
        SecondsToClosing,
        IsReserveMet,
      } = response;
      if (IsClosed) {
        EVENT_BUS.dispatch(AUCTION_EVENTS.AUCTION_CLOSED, {
          userBidderId,
          bidderIds: BidderIds,
          secondsToClosing: SecondsToClosing,
          isClosed: IsClosed,
          closeCompleted: true,
          isReserveMet: IsReserveMet,
        });

        if (BidderId === userBidderId) {
          EVENT_BUS.dispatch(AUCTION_EVENTS.CURRENT_USER_WON, {
            isReserveMet: IsReserveMet,
          });
        } else if (userBidderId) {
          // userBidderId exist -> user took part in bidding and
          // modal about not winning should be shown
          EVENT_BUS.dispatch(AUCTION_EVENTS.CURRENT_USER_NOT_WON, {
            isReserveMet: IsReserveMet,
          });
        }
      } else if (retryCount < RETRY_DELAYS.length) {
        appInsights.trackEvent({
          name: 'Auction Closed Status Check',
          properties: {
            AuctionPropertyId: auctionPropertyId,
            RetryCount: retryCount + 1,
          },
        });
        setTimeout(
          () =>
            checkIfAuctionIsClosedAndUpdateUI({ retryCount: retryCount + 1 }),
          RETRY_DELAYS[retryCount]
        );
      } else {
        EVENT_BUS.dispatch(AUCTION_EVENTS.REQUEST_AUCTION_STATUS_UPDATE);
      }
    });
  };

  EVENT_BUS.on(
    AUCTION_EVENTS.AUCTION_TIME_ENDED,
    checkIfAuctionIsClosedAndUpdateUI
  );

  EVENT_BUS.on(
    AUCTION_EVENTS.CONNECTION_ESTABLISHED,
    ({ timeout = NO_TIMEOUT }) => {
      setTimeout(
        () => EVENT_BUS.dispatch(AUCTION_EVENTS.CLEAR_NOTIFICATION),
        timeout
      );

      EVENT_BUS.dispatch(AUCTION_EVENTS.REQUEST_AUCTION_STATUS_UPDATE, {
        timeout,
      });
    }
  );

  // clear notification after timeout
  EVENT_BUS.on(AUCTION_EVENTS.SHOW_NOTIFICATION, ({ notification }) => {
    if (NOTIFICATIONS_TO_BE_CLEARED.includes(notification)) {
      setTimeout(
        () => EVENT_BUS.dispatch(AUCTION_EVENTS.CLEAR_NOTIFICATION),
        DEFAULT_TIMEOUT
      );
    }
  });

  EVENT_BUS.on(WINDOW_EVENTS.TAB_FOCUS, () => {
    EVENT_BUS.dispatch(AUCTION_EVENTS.CONNECTION_CHECK);
  });

  EVENT_BUS.on(AUCTION_EVENTS.CONNECTION_CHECK, (_, context) => {
    const connectionCheck = () =>
      new Promise((resolve) => {
        const isDisconnected =
          hub.state !== $.signalR.connectionState.connected;
        if (!context.isClosed && isDisconnected) {
          newHubConnection().then(resolve);
        } else {
          resolve();
        }
      });

    connectionCheck().then(() => {
      if (!context.closeCompleted) {
        checkIfAuctionIsClosedAndUpdateUI();
      }
    });
  });

  EVENT_BUS.on(AUCTION_EVENTS.AUCTION_CLOSED, () => {
    hubHandlers.stop();
    hub.stop();
  });

  const hubHandlers = createScopedHubHandlers({
    hub,
    userBidderId,
    auctionPropertyId,
    newHubConnection,
    reloadPage,
  });

  hub.reconnecting(hubHandlers.reconnecting);
  hub.reconnected(hubHandlers.reconnected);
  hub.connectionSlow(hubHandlers.connectionSlow);
  hub.disconnected(hubHandlers.disconnected);
  hub.error(hubHandlers.error);

  assignHandlers(
    client,
    createClientHandlers({
      userBidderId,
      auctionPropertyId,
      stopBidRetry,
    })
  );
  newHubConnection();
};
